// Owns every live native `Analysis` handle the editor services hold, so that
// no caller ever has to remember to dispose one and the module's own limit of
// 64 live handles (`crates/hollowscript-wasm/web/README.md`, "The analysis
// handle") can never be reached by an editor.
//
// The rules, all of them enforced here and nowhere else:
//
//   - One live analysis per key. A key is one editor model (or any other
//     "document"): asking for a different text under the same key disposes the
//     old handle first and checks the new one, so a key never holds two.
//   - The same input again is a cache hit. Source, entry path, capability
//     declarations and the sibling modules are all part of the input, so a
//     hit is always an analysis of exactly what was asked about.
//   - A global ceiling, `MAX_LIVE_ANALYSES`, well under the module's 64. When
//     a new analysis would pass it, the least recently used *other* key is
//     disposed (its next request simply re-analyses). The key being served is
//     never evicted, so a handle a caller has just been given is never
//     disposed under it.
//   - `release(key)` disposes a key's handle, for a model that is going away.
//
// Callers use a handle only synchronously, straight after `acquire` returns
// it (`withAnalysis`): another `acquire` for the same key disposes it, and the
// wasm calls themselves are synchronous, so nothing can interleave inside one
// block. Keeps no `monaco-editor` import (see `tokenize.ts`).

import type { Analysis, NativeCapabilityDeclaration, NativeModules } from '../native/hollowscript.js';
import type { MonacoTooling } from '../nativeTooling.js';

/** The ceiling this pool keeps itself under. The module refuses a 65th live
 * analysis; half of that leaves room for a script roster larger than anyone
 * keeps open and for the catalog's own transient probe. */
export const MAX_LIVE_ANALYSES = 32;

export interface AnalysisInput {
  readonly source: string;
  /** The entry module's own canonical path (`util.hws`). */
  readonly entryPath: string;
  /** The host capabilities declared beyond the standard ones. */
  readonly declarations: readonly NativeCapabilityDeclaration[];
  /** The other modules an `import` resolves against, by canonical path. */
  readonly modules: NativeModules;
  /** A stable identity for `declarations` (the sorted capability names that
   * produced them), so equality of two inputs need not walk the declaration
   * objects themselves. */
  readonly declarationsKey: string;
}

interface Entry {
  readonly analysis: Analysis;
  readonly input: AnalysisInput;
  lastUsed: number;
}

function sameModules(a: NativeModules, b: NativeModules): boolean {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

function sameInput(a: AnalysisInput, b: AnalysisInput): boolean {
  return a.source === b.source && a.entryPath === b.entryPath && a.declarationsKey === b.declarationsKey && sameModules(a.modules, b.modules);
}

export class AnalysisPool {
  readonly #analyse: MonacoTooling['analyse'];
  readonly #max: number;
  readonly #entries = new Map<string, Entry>();
  #clock = 0;

  constructor(tooling: Pick<MonacoTooling, 'analyse'>, max: number = MAX_LIVE_ANALYSES) {
    this.#analyse = (source, entryName, capabilities, modules) => tooling.analyse(source, entryName, capabilities, modules);
    this.#max = Math.max(1, max);
  }

  /** How many handles are live right now. */
  get liveCount(): number {
    return this.#entries.size;
  }

  has(key: string): boolean {
    return this.#entries.has(key);
  }

  /** The analysis of exactly `input` under `key`, checking it if the key
   * holds anything else. `undefined` when the module refuses (a malformed
   * argument, an oversize source): the editor services answer nothing then,
   * rather than throwing into Monaco on every keystroke. */
  acquire(key: string, input: AnalysisInput): Analysis | undefined {
    const existing = this.#entries.get(key);
    if (existing && sameInput(existing.input, input)) {
      existing.lastUsed = ++this.#clock;
      return existing.analysis;
    }
    // The old handle goes before the new one is made: a key never holds two.
    if (existing) this.release(key);
    while (this.#entries.size >= this.#max) this.#evictLeastRecentlyUsed();

    let analysis: Analysis;
    try {
      analysis = this.#analyse(input.source, input.entryPath, input.declarations, input.modules);
    } catch (error) {
      // The module's own cap is the one failure worth a second attempt: if
      // it was reached (something outside this pool holds handles), make
      // room by dropping everything this pool holds and try once more.
      if (!(error instanceof Error) || !/dispose/.test(error.message) || this.#entries.size === 0) return undefined;
      this.disposeAll();
      try {
        analysis = this.#analyse(input.source, input.entryPath, input.declarations, input.modules);
      } catch {
        return undefined;
      }
    }
    this.#entries.set(key, { analysis, input, lastUsed: ++this.#clock });
    return analysis;
  }

  /** Disposes `key`'s handle, if it has one. Safe to call twice. */
  release(key: string): void {
    const entry = this.#entries.get(key);
    if (!entry) return;
    this.#entries.delete(key);
    entry.analysis.dispose();
  }

  /** Disposes every handle this pool holds. */
  disposeAll(): void {
    for (const key of [...this.#entries.keys()]) this.release(key);
  }

  #evictLeastRecentlyUsed(): void {
    let oldestKey: string | undefined;
    let oldest = Infinity;
    for (const [key, entry] of this.#entries) {
      if (entry.lastUsed < oldest) {
        oldest = entry.lastUsed;
        oldestKey = key;
      }
    }
    if (oldestKey !== undefined) this.release(oldestKey);
  }
}
